import * as React from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { companiesClient, apiClientRegistry } from '@/lib/api'
import type { CompanyResponse } from '@super/shared-types'
import { toast } from 'sonner'
import { isSuperAdmin } from '@/lib/auth-utils'
import { useSuperAdminCompany } from '@/hooks/use-super-admin-company'

interface AppAuthContextValue {
  // User and company state
  user: any // WorkOS user type
  currentCompany: CompanyResponse | null
  isSuperAdmin: boolean

  // Loading states
  isAuthLoading: boolean // From WorkOS
  isCompanyLoading: boolean // When fetching company data
  isInitialized: boolean // After first auth + company load

  // Actions
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  switchSuperAdminCompany: (companyId: string) => Promise<void>
  updateCompany: (companyId: string, data: any) => Promise<CompanyResponse>
  getAccessToken: () => Promise<string | null>
}

const AppAuthContext = React.createContext<AppAuthContextValue | null>(null)

interface AppAuthProviderProps {
  children: React.ReactNode
}

export function AppAuthProvider({ children }: AppAuthProviderProps) {
  // Use WorkOS React hooks
  const {
    user,
    isLoading: authLoading,
    getAccessToken,
    signIn,
    signOut,
  } = useAuth()

  // Use the super admin company hook
  const { selectedCompanyId, setSelectedCompanyId, clearSelectedCompany } =
    useSuperAdminCompany()

  const [currentCompany, setCurrentCompany] =
    React.useState<CompanyResponse | null>(null)
  const [currentAccessToken, setCurrentAccessToken] = React.useState<
    string | null
  >(null)
  const [isCompanyLoading, setIsCompanyLoading] = React.useState(false)
  const [isInitialized, setIsInitialized] = React.useState(false)

  // Compute super admin status from current access token
  const currentIsSuperAdmin = React.useMemo(() => {
    return currentAccessToken ? isSuperAdmin(currentAccessToken) : false
  }, [currentAccessToken])

  // Initialize auth state when user is available
  React.useEffect(() => {
    const initialize = async () => {
      try {
        if (user) {
          // Get access token
          const accessToken = await getAccessToken()

          if (accessToken) {
            // Set auth token for all API clients via registry
            apiClientRegistry.setAuthToken(accessToken)
            setCurrentAccessToken(accessToken)

            // Check super admin status from JWT payload
            const hasSuperAdminRole = isSuperAdmin(accessToken)

            // Load current company
            setIsCompanyLoading(true)
            try {
              if (hasSuperAdminRole && selectedCompanyId) {
                try {
                  // Load the selected company for super admin
                  const company =
                    await companiesClient.getCompanyById(selectedCompanyId)
                  setCurrentCompany(company)
                } catch (error) {
                  console.error(
                    'Error loading selected company for Admin:',
                    error,
                  )
                  clearSelectedCompany()
                  const company = await companiesClient.getCurrentCompany()
                  setCurrentCompany(company)
                }
              } else {
                const company = await companiesClient.getCurrentCompany()
                setCurrentCompany(company)
              }
            } catch (error) {
              console.error('Error loading company:', error)
              setCurrentCompany(null)
            } finally {
              setIsCompanyLoading(false)
            }
          }
        } else {
          // Clear state when no user
          setCurrentCompany(null)
          setCurrentAccessToken(null)
          clearSelectedCompany()
          apiClientRegistry.clearAuthTokens()
        }
      } catch (error) {
        console.error('Error initializing app auth:', error)
        setCurrentCompany(null)
        setCurrentAccessToken(null)
        toast.error('Failed to load user information')
      } finally {
        setIsInitialized(true)
      }
    }

    if (!authLoading) {
      initialize()
    }
  }, [user, authLoading, getAccessToken])

  const handleSignOut = React.useCallback(async () => {
    try {
      await signOut()

      // Clear local state
      setCurrentCompany(null)
      setCurrentAccessToken(null)
      clearSelectedCompany()
      apiClientRegistry.clearAuthTokens()

      toast.success('Signed out successfully')
    } catch (error) {
      console.error('Error signing out:', error)
      toast.error('Failed to sign out')
    }
  }, [signOut, clearSelectedCompany])

  const handleGetAccessToken = React.useCallback(async () => {
    return await getAccessToken()
  }, [getAccessToken])

  const switchSuperAdminCompany = React.useCallback(
    async (companyId: string) => {
      if (!currentIsSuperAdmin) {
        toast.error('Only Super Admins can switch companies')
        return
      }

      setIsCompanyLoading(true)
      try {
        // Fetch the full company data by ID
        const company = await companiesClient.getCompanyById(companyId)

        // Only update company and selected ID after successful fetch
        setCurrentCompany(company)
        setSelectedCompanyId(companyId)

        toast.success(`Switched to ${company.name}`, {
          description: 'Viewing as Super Data Admin',
        })
      } catch (error) {
        console.error('Error switching company:', error)
        toast.error('Failed to switch company')
      } finally {
        setIsCompanyLoading(false)
      }
    },
    [currentIsSuperAdmin, setSelectedCompanyId],
  )

  const updateCompany = React.useCallback(
    async (companyId: string, data: any) => {
      try {
        const updatedCompany = await companiesClient.updateCompany(
          companyId,
          data,
        )
        // Update local state if this is the current company
        if (currentCompany?.id === companyId) {
          setCurrentCompany(updatedCompany)
        }
        return updatedCompany
      } catch (error) {
        console.error('Error updating company:', error)
        throw error
      }
    },
    [currentCompany?.id],
  )

  const value: AppAuthContextValue = React.useMemo(
    () => ({
      // State
      user,
      currentCompany,
      isSuperAdmin: currentIsSuperAdmin,
      isAuthLoading: authLoading,
      isCompanyLoading,
      isInitialized,

      // Actions
      signIn,
      signOut: handleSignOut,
      switchSuperAdminCompany,
      updateCompany,
      getAccessToken: handleGetAccessToken,
    }),
    [
      user,
      currentCompany,
      currentIsSuperAdmin,
      authLoading,
      isCompanyLoading,
      isInitialized,
      signIn,
      handleSignOut,
      switchSuperAdminCompany,
      updateCompany,
      handleGetAccessToken,
    ],
  )

  return (
    <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>
  )
}

export function useAppAuth(): AppAuthContextValue {
  const context = React.useContext(AppAuthContext)

  if (!context) {
    throw new Error('useAppAuth must be used within an AppAuthProvider')
  }

  return context
}
